<template>
  <div class="prioritylist list">
    <div class="prioritylist__entry whitespace-nowrap">
      <div></div>
      <div></div>
      <div></div>
      <span class="label mv-0 ta-r">
        <template v-if="ctx.impactMatrix?.relationType == 'impact'">{{ $t("labels.active") }}</template>
        <template v-else>{{ $t("labels.outflow") }}</template>
      </span>
      <span class="label mv-0">| 
        <template v-if="ctx.impactMatrix?.relationType == 'impact'">{{ $t("labels.passive") }}</template>
        <template v-else>{{ $t("labels.inflow") }}</template>
      </span>
    </div>
    <transition-group name="prioritylist__anim">
      <div
        v-for="(priority, index) in sortedActivePriorities"
        :key="priority.elementUid"
        class="prioritylist__entry"
      >
        <strong class="ta-r pr-2">{{ index + 1 }}</strong>
        <span
          class="prioritylist__elementlabel"
          :class="targetPriorityClasses[priority.elementUid]"
          >{{ elements.labels[priority.elementUid] }}</span
        >
        <span class="prioritylist__elementname">
          {{ elements.byUid[priority.elementUid].title }}
        </span>
        <span class="ta-r">
          ({{ formatNumber(activePriorities[priority.elementUid]) }}
        </span>
        <span>
          |
          {{ formatNumber(passivePriorities[priority.elementUid]) }})
        </span>
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { mapValues } from "lodash-es";
import { useI18n } from "vue-i18n";

import { useElements } from "@editor/items/element/elements";
import { useImpactMatrix } from "@editor/items/impactMatrix/useImpactMatrix";

const props = defineProps<{
  matrixId?: number;
}>();

const { n } = useI18n();
const elements = useElements();

const ctx = useImpactMatrix(() => props.matrixId ?? null);

const sortedActivePriorities = computed(() => ctx.sortedActivePriorities);
const activePriorities = computed(() => ctx.activePriorities);
const passivePriorities = computed(() => ctx.passivePriorities);

const targetPriorityClasses = computed(() => {
  return mapValues(elements.byUid, (element) => {
    let className;
    const active = activePriorities.value[element.uid];
    const passive = passivePriorities.value[element.uid];
    if (active >= 0.5 && passive >= 0.5) className = "is-critical";
    else if (active >= 0.5 && passive < 0.5) className = "is-active";
    else if (active < 0.5 && passive >= 0.5) className = "is-passive";
    else if (active < 0.5 && passive < 0.5) className = "is-buffering";
    return className;
  });
});

function formatNumber(num: number, options = {}) {
  num = Math.floor(num * 100) / 100;
  return n(num, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  })
  .replaceAll(/\s/g, " ");
}
</script>

<style lang="scss">
.prioritylist {
  display: flex;
  flex-direction: column;
  justify-content: center;

  &__entry {
    white-space: nowrap;
    display: grid;
    grid-template-columns: 1.5rem 1.75rem minmax(0, 1fr) 3rem 3rem;
    gap: 0.25em;
    padding: 0.1em 0;
  }

  &__elementname {
    overflow: hidden;
    hyphens: auto;
    text-overflow: ellipsis;
    white-space: normal;
  }

  &__elementlabel {
    --prio-color: var(--color-action-contrast);
    
    background-color: var(--prio-color);
    border-radius: 50rem;
    width: 1.5rem;
    aspect-ratio: 1 / 1;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    margin-top: -1px;
    cursor: default;
    
    &.is-active {
      --prio-color: var(--color-priority-active);
    }
    &.is-critical {
      --prio-color: var(--color-priority-critical);
    }
    &.is-buffering {
      --prio-color: var(--color-priority-buffering);
    }
    &.is-passive {
      --prio-color: var(--color-priority-passive);
    }
  }

  &__anim {
    &-move, /* apply transition to moving elements */
    &-enter-active,
    &-leave-active {
      transition: all 0.5s ease;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateX(30px);
    }

    /* ensure leaving items are taken out of layout flow so that moving
      animations can be calculated correctly. */
    &-leave-active {
      position: absolute;
    }
  }
}
</style>
